/**
 * Vuex Store 通用工具函数
 * 参考 aegismaternity 项目的 Redux 规划模式，适配 Vuex
 */

/**
 * 创建服务类型映射函数（参考 switchPayload 模式）
 * @param {Object} serviceMap - 服务类型到 API 方法的映射
 * @returns {Function} 返回服务类型映射函数
 */
export function createServiceSwitch(serviceMap) {
  return (serviceType, query) => {
    const service = serviceMap[serviceType] || serviceMap.default || serviceMap.undefined
    if (typeof service === 'function') {
      return service(query)
    }
    console.error(`Redux: 请求未定义 serviceType 类型: ${serviceType}`)
    return Promise.reject(new Error(`未定义的服务类型: ${serviceType}`))
  }
}

/**
 * 创建通用的异步 Action（参考 createAsyncThunk 模式）
 * @param {Function|Object} apiMethodOrSwitch - API 方法或服务映射对象
 * @param {String} mutationType - Mutation 类型
 * @param {Function} transformData - 数据转换函数（可选）
 * @param {Object} options - 配置选项
 * @returns {Function} Action 函数
 */
export function createAsyncAction(apiMethodOrSwitch, mutationType, transformData = null, options = {}) {
  const {
    pendingMutation = null,
    errorMutation = null,
    serviceType = null
  } = options

  return async ({ commit, state }, params = {}) => {
    try {
      // 支持服务类型切换
      let apiMethod
      if (typeof apiMethodOrSwitch === 'function') {
        apiMethod = apiMethodOrSwitch
      } else if (typeof apiMethodOrSwitch === 'object') {
        const switchFn = createServiceSwitch(apiMethodOrSwitch)
        const serviceType = params.serviceType || 'default'
        apiMethod = switchFn(serviceType, params.query || params)
      } else {
        throw new Error('apiMethodOrSwitch 必须是函数或对象')
      }

      // 触发 pending 状态
      if (pendingMutation) {
        commit(pendingMutation, { serviceType: params.serviceType, query: params.query })
      }

      // 调用 API
      const res = await apiMethod(params.query || params)

      // 处理响应
      if (res.errcode === 1) {
        const data = transformData ? transformData(res.data, params) : res.data
        commit(mutationType, {
          data,
          serviceType: params.serviceType,
          query: params.query || params,
          ...params
        })
        return { success: true, data: res.data, errcode: 1 }
      } else {
        const error = res.errinfo || '操作失败'
        if (errorMutation) {
          commit(errorMutation, { error, serviceType: params.serviceType })
        }
        return { success: false, message: error, errcode: res.errcode }
      }
    } catch (error) {
      console.error('[Async Action Error]:', error)
      const errorMsg = error.message || '网络错误，请重试'
      if (errorMutation) {
        commit(errorMutation, { error: errorMsg, serviceType: params.serviceType })
      }
      return { success: false, message: errorMsg, error }
    }
  }
}

/**
 * 创建数据格式化函数（参考 formatData 模式）
 * @param {Object} config - 配置对象
 * @param {Array} config.keyConfig - 字段映射配置
 * @param {Object} config.defaultKey - 默认字段映射
 * @param {Function} config.processor - 数据处理函数
 * @returns {Function} 数据格式化函数
 */
export function createDataFormatter({ keyConfig = [], defaultKey = {}, processor = null }) {
  return ({ data, serviceType = 'default', dataType = 'default', query = {} }) => {
    if (!Array.isArray(data) && typeof data !== 'object') {
      return data
    }

    // 获取字段映射
    const fieldMap = keyConfig.find(kc => kc.key === serviceType) || defaultKey

    // 如果提供了自定义处理器
    if (processor) {
      return processor({ data, serviceType, dataType, query, fieldMap })
    }

    // 默认处理：直接返回数据
    return data
  }
}

/**
 * 创建通用的列表 Action（带加载状态和错误处理）
 * @param {Function|Object} apiMethodOrSwitch - API 方法或服务映射
 * @param {String} setListMutation - 设置列表的 mutation
 * @param {Object} options - 配置选项
 * @returns {Function} Action 函数
 */
export function createListAction(apiMethodOrSwitch, setListMutation, options = {}) {
  const {
    pendingMutation = 'SET_LOADING',
    errorMutation = 'SET_ERROR',
    transformData = null
  } = options

  return createAsyncAction(
    apiMethodOrSwitch,
    setListMutation,
    transformData,
    {
      pendingMutation,
      errorMutation
    }
  )
}

/**
 * 创建通用的提交 Action
 * @param {Function} apiMethod - API 方法
 * @param {String} successMessage - 成功提示信息
 * @param {Function} onSuccess - 成功回调（可选）
 * @returns {Function} Action 函数
 */
export function createSubmitAction(apiMethod, successMessage = '操作成功', onSuccess = null) {
  return async ({ commit }, params) => {
    try {
      const res = await apiMethod(params)
      if (res.errcode === 1) {
        if (onSuccess) {
          onSuccess({ commit }, res.data)
        }
        return { success: true, data: res.data, message: successMessage, errcode: 1 }
      } else {
        return { success: false, message: res.errinfo || '操作失败', errcode: res.errcode }
      }
    } catch (error) {
      console.error('[Submit Action Error]:', error)
      return { success: false, message: error.message || '网络错误，请重试', error }
    }
  }
}

/**
 * 从 Cookie 恢复数据到 Store
 * @param {Object} store - Vuex store 实例
 * @param {Object} cookieMap - Cookie 键名到 state 键名的映射
 * @param {Object} cookies - Cookie 对象
 */
export function restoreFromCookie(store, options, cookieMap, cookies) {
  const {
    module = null,
    mutation = null
  } = options || {}

  const payload = {}
  Object.keys(cookieMap).forEach(cookieKey => {
    const stateKey = cookieMap[cookieKey]
    const value = cookies.get(cookieKey)
    if (value) {
      payload[stateKey] = value
    }
  })

  if (Object.keys(payload).length > 0) {
    // 必须显式传入 mutation，避免与具体业务（如登录）强绑定
    if (!mutation) {
      console.error('[restoreFromCookie] mutation 未指定，已跳过提交。')
      return
    }
    const type = module ? `${module}/${mutation}` : mutation
    store.commit(type, payload)
  }
}

/**
 * 同步 Store 数据到 Cookie
 * @param {Object} store - Vuex store 实例
 * @param {Object} cookieMap - state 键名到 Cookie 键名的映射
 * @param {Object} cookies - Cookie 对象
 */
export function syncToCookie(store, cookieMap, cookies) {
  const state = store.state
  Object.keys(cookieMap).forEach(stateKey => {
    const cookieKey = cookieMap[stateKey]
    const value = state[stateKey]
    if (value !== undefined && value !== null && value !== '') {
      cookies.set(cookieKey, value)
    }
  })
}



// WEBPACK FOOTER //
// ./src/store/utils.js